import { useState, useRef } from "react";
import { Link } from "react-router-dom";

const LINES = [
  ["A", "A is for Always, the promise he made."],
  ["B", "B is for Breath that the bowl gently takes."],
  ["C", "C is for Color that drains when we play."],
  ["D", "D is for Doorways that open in rain."],
  ["E", "E is for Echo, the sound of a name."],
  ["F", "F is for Feathers that fall when he fades."],
  ["G", "G is for Growing, the thing he won't do."],
  ["H", "H is for Hook, who remembers the truth."],
  ["I", "I is for Innocence stolen in two."],
  ["J", "J is for John, singing bright in the blue."],
  ["K", "K is for Kensington, myths that come through."],
  ["L", "L is for Lost Boys, who don't know they're used."],
  ["M", "M is for Mary, who sees both the frames."],
  ["N", "N is for Never, the cost of the game."],
  ["O", "O is for Open, the vowel that remains."],
  ["P", "P is for Pan, who is wave and is pain."],
  ["Q", "Q is for Quiet, the silence that came."],
  ["R", "R is for Resonance, humming the same."],
  ["S", "S is for Sand, forming stars in the bowl."],
  ["T", "T is for Tink, keeping love on control."],
  ["U", "U is for Under, the world that he stole."],
  ["V", "V is for Vapor, the Everlit glow."],
  ["W", "W is Workhouse, where shadows still go."],
  ["X", "X is the Crossing, where two versions show."],
  ["Y", "Y is the Yielding, the backward tone's plea."],
  ["Z", "Z is the Zero, the end of the dream."],
];

// Vowels are minor keys, consonants are major keys
// Using different octaves for variety across the alphabet
const VOWELS = ['A', 'E', 'I', 'O', 'U'];

// Piano frequencies - A, E, I, O, U get minor chords, rest get major
const getPianoNote = (letter) => {
  const isVowel = VOWELS.includes(letter);
  const letterIndex = letter.charCodeAt(0) - 65; // 0-25
  
  // Base frequencies for different octaves (C3 to C5 range)
  const baseFreq = 130.81; // C3
  
  // Map letters to different notes across octaves
  const noteIndex = letterIndex % 12;
  const octaveOffset = Math.floor(letterIndex / 12);
  const freq = baseFreq * Math.pow(2, (noteIndex + octaveOffset * 12) / 12);
  
  return { freq, isMinor: isVowel };
};

// Play a piano-like tone
const playPianoNote = (audioCtx, letter) => {
  const { freq, isMinor } = getPianoNote(letter);
  
  // Create oscillators for chord
  const oscillator1 = audioCtx.createOscillator();
  const oscillator2 = audioCtx.createOscillator();
  const oscillator3 = audioCtx.createOscillator();
  const gainNode = audioCtx.createGain();
  
  // Root note
  oscillator1.frequency.setValueAtTime(freq, audioCtx.currentTime);
  oscillator1.type = 'sine';
  
  // Third (minor = 3 semitones, major = 4 semitones)
  const thirdRatio = isMinor ? Math.pow(2, 3/12) : Math.pow(2, 4/12);
  oscillator2.frequency.setValueAtTime(freq * thirdRatio, audioCtx.currentTime);
  oscillator2.type = 'sine';
  
  // Fifth (7 semitones)
  const fifthRatio = Math.pow(2, 7/12);
  oscillator3.frequency.setValueAtTime(freq * fifthRatio, audioCtx.currentTime);
  oscillator3.type = 'sine';
  
  // Piano-like envelope
  gainNode.gain.setValueAtTime(0, audioCtx.currentTime);
  gainNode.gain.linearRampToValueAtTime(0.3, audioCtx.currentTime + 0.02);
  gainNode.gain.exponentialRampToValueAtTime(0.15, audioCtx.currentTime + 0.1);
  gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 1.5);
  
  oscillator1.connect(gainNode);
  oscillator2.connect(gainNode);
  oscillator3.connect(gainNode);
  gainNode.connect(audioCtx.destination);
  
  oscillator1.start();
  oscillator2.start();
  oscillator3.start();
  
  oscillator1.stop(audioCtx.currentTime + 1.5);
  oscillator2.stop(audioCtx.currentTime + 1.5);
  oscillator3.stop(audioCtx.currentTime + 1.5);
};

function Frame({ id, title }) {
  return (
    <div style={{ position: "relative", width: "100%", paddingBottom: "56.25%", height: 0, overflow: "hidden", borderRadius: 12, background: "#000" }}>
      <iframe
        src={`https://www.youtube.com/embed/${id}?rel=0`}
        title={title}
        style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: 0 }}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    </div>
  );
}

export default function AbcPage() {
  const [open, setOpen] = useState(null);
  const [activeKey, setActiveKey] = useState(null);
  const audioCtxRef = useRef(null);
  const line = LINES.find((row) => row[0] === open);

  const handleLetterClick = (letter, isOpen) => {
    // Initialize audio context on first interaction
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume();
    }
    
    // Play piano note
    playPianoNote(audioCtxRef.current, letter);
    setActiveKey(letter);
    setTimeout(() => setActiveKey(null), 200);
    
    // Toggle open state
    setOpen(isOpen ? null : letter);
  };

  const isVowel = (letter) => VOWELS.includes(letter);

  return (
    <main style={{ background: "#0A0605", color: "#F3EAD3", minHeight: "100vh", padding: "48px 20px 80px" }} data-testid="abc-page">
      <div style={{ maxWidth: 960, margin: "0 auto" }}>
        <Link to="/dark-pan" style={{ color: "#C9A227", fontSize: 14, display: "inline-block", marginBottom: 24 }}>← Back to The Dark Pan</Link>
        
        <p style={{ letterSpacing: "0.28em", textTransform: "uppercase", color: "#C9A227", fontSize: 12 }}>The primer</p>
        <h1 style={{ fontSize: 56, margin: "12px 0", fontFamily: "Playfair Display, serif" }}>tink tinker Pan</h1>
        <p style={{ color: "#B7A99A", maxWidth: 640 }}>A is for Always. The primer of the film. Press play.</p>

        <div style={{ marginTop: 28 }}>
          <Frame id="cse3qufxz2g" title="ABC song" />
          <p style={{ marginTop: 12 }}>
            <a href="https://youtu.be/cse3qufxz2g" target="_blank" rel="noopener noreferrer" style={{ color: "#C9A227" }}>Watch on YouTube</a>
          </p>
        </div>

        <p style={{ marginTop: 40, color: "#B7A99A" }}>
          Touch a letter. A through Z. 
          <span style={{ display: "block", fontSize: 12, marginTop: 4, opacity: 0.7 }}>
            🎹 Vowels (A, E, I, O, U) play minor chords · Consonants play major chords
          </span>
        </p>
        <ul style={{ listStyle: "none", padding: 0, marginTop: 16, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 8 }}>
          {LINES.map(([letter, text]) => {
            const on = open === letter;
            const isPressed = activeKey === letter;
            const vowel = isVowel(letter);
            return (
              <li key={letter}>
                <button
                  type="button"
                  onClick={() => handleLetterClick(letter, on)}
                  style={{
                    width: "100%",
                    minHeight: 96,
                    textAlign: "left",
                    padding: 12,
                    borderRadius: 8,
                    cursor: "pointer",
                    color: "#F3EAD3",
                    background: isPressed 
                      ? (vowel ? "rgba(147, 112, 219, 0.3)" : "rgba(201, 162, 39, 0.3)")
                      : on 
                        ? (vowel ? "rgba(147, 112, 219, 0.15)" : "rgba(201,162,39,0.12)") 
                        : "#161311",
                    border: on 
                      ? `1px solid ${vowel ? "rgba(147, 112, 219, 0.6)" : "rgba(201,162,39,0.5)"}` 
                      : "1px solid rgba(243,234,211,0.12)",
                    transition: "all 0.15s ease",
                    transform: isPressed ? "scale(0.97)" : "scale(1)",
                    boxShadow: isPressed ? "inset 0 2px 4px rgba(0,0,0,0.3)" : "none"
                  }}
                  data-testid={`letter-${letter}`}
                >
                  <span style={{ 
                    display: "flex", 
                    alignItems: "center", 
                    gap: 8,
                    fontSize: 28, 
                    fontFamily: "Playfair Display, serif" 
                  }}>
                    {letter}
                    <span style={{ 
                      fontSize: 10, 
                      padding: "2px 6px", 
                      borderRadius: 4, 
                      background: vowel ? "rgba(147, 112, 219, 0.3)" : "rgba(201, 162, 39, 0.2)",
                      color: vowel ? "#b19cd9" : "#C9A227"
                    }}>
                      {vowel ? "minor" : "major"}
                    </span>
                  </span>
                  <span style={{ display: "block", marginTop: 6, fontSize: 12, color: "#B7A99A" }}>
                    {on ? text : text.split(" ").slice(0, 3).join(" ") + "…"}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
        {line ? <p style={{ marginTop: 24, fontSize: 28, fontStyle: "italic", fontFamily: "Playfair Display, serif" }}>{line[1]}</p> : null}
      </div>
    </main>
  );
}
